import type {
  MpBenchRow,
  MpKpi,
  MpPlaybookStep,
  MpProfileCompleteness,
  MpReviewAuditItem,
  MpVectorData,
} from './types';

export const MP_KPIS: MpKpi[] = [
  {
    label: 'Map Pack win rate',
    value: '45%',
    tone: 'ai',
    sub: 'Top 3 for 45% of tracked keywords',
  },
  {
    label: 'Review deficit',
    value: '-265',
    tone: 'terra',
    sub: 'behind Smile Central (450 vs 185)',
  },
  {
    label: '30-day velocity winner',
    value: 'Smile Central',
    tone: 'gold',
    sub: '+12 reviews this month (you: +4)',
    small: true,
  },
  {
    label: 'Est. patients from Maps',
    value: '~140',
    tone: 'success',
    sub: 'clicks to website / calls from GBP',
  },
];

export const MP_BENCH: MpBenchRow[] = [
  { metric: 'Star rating', yours: '4.6', comp: '4.8', avg: '4.3', star: true, winner: 'comp' },
  { metric: 'Total reviews', yours: '185', comp: '450', avg: '120', winner: 'comp' },
  { metric: '30-day review velocity', yours: '+4', comp: '+12', avg: '+2', winner: 'comp' },
  { metric: 'Review response rate', yours: '65%', comp: '92%', avg: '40%', winner: 'comp' },
  { metric: 'Avg response time', yours: '4 days', comp: '< 24 hours', avg: 'N/A', winner: 'comp' },
  { metric: 'GBP photos (last 90 days)', yours: '0', comp: '15', avg: '3', winner: 'comp' },
  { metric: 'Active GBP posts / offers', yours: 'None', comp: '$99 Exam', avg: '—', winner: 'comp' },
  { metric: 'Avg Map Pack position', yours: '#4', comp: '#1', avg: '#5', winner: 'comp' },
  { metric: 'Website click-through rate', yours: '18%', comp: '24%', avg: '12%', winner: 'comp' },
  { metric: 'Phone calls from GBP', yours: '~45/mo', comp: '~95/mo', avg: '~25/mo', winner: 'comp' },
];

export const MP_ATTACK: MpVectorData = {
  yourPraise: ['Friendly Support Staff', 'Painless Experience', 'Transparent Pricing', 'Great with Kids'],
  theirComplaints: ['Surprise Billing', 'Long Waiting Room Time', 'Billing Errors', 'Rude Front Desk'],
  note: 'Their patients complain about surprise billing and billing errors. Run an ad or social post highlighting your transparent, upfront pricing. Mention “no surprise bills” in your GBP description — you can directly capture their frustrated patients.',
};

export const MP_THREAT: MpVectorData = {
  theirPraise: ['Modern Equipment', 'Fast Scheduling', 'Clean Office', 'Online Booking'],
  yourComplaints: ['Hard to Reach by Phone', 'Outdated Waiting Room', 'Limited Weekend Hours'],
  note: "They're winning on convenience and technology perception. Add an online booking widget to your GBP and website, upload fresh office photos showing your equipment, and consider Saturday afternoon hours — 3 competitors recently extended weekend coverage.",
};

export const MP_YOUR_PROFILE: MpProfileCompleteness = {
  label: 'Your profile',
  percent: 70,
  tone: 'terra',
  items: [
    { label: 'Business Description', done: true },
    { label: 'Service List (all services)', done: true },
    { label: 'Office Hours', done: true },
    { label: 'Phone & Website', done: true },
    { label: 'Insurance List', done: false },
    { label: 'Photos (last 90 days)', done: false, detail: '0 uploaded' },
    { label: 'Active Offers / Posts', done: false, detail: 'None active' },
    { label: 'Q&A Section', done: false, detail: '0 answered' },
    { label: 'Appointment Link', done: false },
    { label: 'Spanish Description', done: false },
  ],
};

export const MP_COMP_PROFILE: MpProfileCompleteness = {
  label: 'Smile Central (#1 competitor)',
  percent: 95,
  tone: 'success',
  items: [
    { label: 'Business Description', done: true },
    { label: 'Service List (all services)', done: true },
    { label: 'Office Hours', done: true },
    { label: 'Phone & Website', done: true },
    { label: 'Insurance List', done: true },
    { label: 'Photos (last 90 days)', done: true, detail: '15 uploaded' },
    { label: 'Active Offers / Posts', done: true, detail: '$99 Exam offer' },
    { label: 'Q&A Section', done: true, detail: '10+ answered' },
    { label: 'Appointment Link', done: true },
    { label: 'Spanish Description', done: false },
  ],
};

export const MP_REVIEW_AUDIT: MpReviewAuditItem[] = [
  { value: '14', tone: 'terra', label: 'Unanswered reviews', sub: 'oldest: 6 months ago' },
  { value: '3', tone: 'gold', label: 'Negative reviews unanswered', sub: 'highest visibility risk' },
  { value: '65%', tone: 'success', label: 'Current response rate', sub: 'target: 90%+ (Smile Central: 92%)' },
];

export const MP_PLAYBOOK: MpPlaybookStep[] = [
  {
    num: 1,
    title: 'Activate review requests',
    urgency: 'This week',
    desc: "You need ~4 new reviews per week just to maintain the current gap. Turn on ELVA's automated SMS review requests for post-appointment patients — this alone can 3x your review velocity.",
  },
  {
    num: 2,
    title: 'Reply to 14 open reviews',
    urgency: 'This week',
    cta: 'Review & approve responses',
    desc: 'You have 14 unanswered reviews from the last 6 months — including 3 negative ones. ELVA has already drafted personalized responses for each.',
  },
  {
    num: 3,
    title: 'Upload office photos',
    urgency: 'This week',
    desc: 'Smile Central’s patients praise their “modern equipment.” Upload 5 new, brightly lit photos of your operatories, waiting area, and team. Google rewards profiles with recent photos.',
  },
  {
    num: 4,
    title: 'Publish a GBP offer',
    urgency: 'Next week',
    cta: 'Create post',
    desc: 'Smile Central has an active $99 Exam offer. Create a comparable offer and ELVA will publish it as a GBP post and sync it with your Google Ads.',
  },
  {
    num: 5,
    title: 'Answer GBP questions',
    urgency: 'Next week',
    cta: 'Review Q&As',
    desc: 'Smile Central has 10+ answered Q&As. Your section is empty. ELVA prepared 6 common Q&As from your practice profile — just approve them.',
  },
];
